import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCatalogue, type CatalogueEntry } from '../data/problems';
import { useFocusTrap } from '../lib/useFocusTrap';

// Hard-cap so the result list stays one viewport tall and arrow-keying never
// scrolls the modal. If we ever ship reading more results, switch to virtual
// scrolling rather than raising this.
const MAX_RESULTS = 10;

const DIFF_PILL: Record<string, string> = {
  Easy: 'cream',
  Medium: 'amber',
  Hard: 'coral',
};

const TYPE_LABEL: Record<string, string> = {
  algorithm: 'algo',
  class: 'class',
  react: 'react',
};

interface Props {
  onClose: () => void;
}

interface ScoredEntry {
  entry: CatalogueEntry;
  score: number;
}

// Score each catalogue row against the query's tokens. A row is in the result
// set only if every token matches *somewhere* (AND semantics). Title hits are
// weighted higher than skill / tag / summary hits so "two sum" ranks Two Sum
// above any tutorial that merely mentions sums.
function scoreEntry(entry: CatalogueEntry, tokens: string[]): number {
  const title = entry.title.toLowerCase();
  const skill = entry.skillId.toLowerCase();
  const tags = (entry.tags ?? []).join(' ').toLowerCase();
  const summary = (entry.summary ?? '').toLowerCase();
  const haystack = `${title} ${skill} ${tags} ${summary}`;

  let score = 0;
  for (const t of tokens) {
    if (!haystack.includes(t)) return -1;
    if (title.includes(t)) score += 10;
    if (skill.includes(t)) score += 3;
    if (tags.includes(t)) score += 2;
    score += 1;
  }
  return score;
}

const SearchModal: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef, true);

  // Snapshot the catalogue once per mount — it never changes during a session
  // pre-P6.4. Post-P6.4 it'll be the API-loaded catalogue; same call site.
  const catalogue = useMemo(() => getCatalogue(), []);

  const results = useMemo<CatalogueEntry[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty query: show a default sampling so the modal isn't blank.
      return catalogue.slice(0, MAX_RESULTS);
    }
    const tokens = q.split(/\s+/).filter(Boolean);
    const scored: ScoredEntry[] = [];
    for (const entry of catalogue) {
      const s = scoreEntry(entry, tokens);
      if (s >= 0) scored.push({ entry, score: s });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, MAX_RESULTS).map(s => s.entry);
  }, [query, catalogue]);

  // Auto-focus the input on open. Plain useEffect — no SSR concerns.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Reset selection to the top whenever the query changes, otherwise the
  // highlighted row can land on a deleted entry as results shrink.
  useEffect(() => {
    setFocusIdx(0);
  }, [query]);

  // Global keyboard handlers. ↑/↓ move selection, Enter opens, Esc closes.
  // We deliberately listen on window (not the input) so arrow keys work even
  // when the user has tabbed off the field momentarily.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown') {
        setFocusIdx(i => Math.min(results.length - 1, i + 1));
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        setFocusIdx(i => Math.max(0, i - 1));
        e.preventDefault();
      } else if (e.key === 'Enter') {
        const r = results[focusIdx];
        if (r) {
          onClose();
          navigate(`/problem/${r.id}`);
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [results, focusIdx, onClose, navigate]);

  // Keep the focused row in view as the user arrow-keys down.
  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>(`[data-row-idx="${focusIdx}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [focusIdx]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search problems"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(30, 27, 22, 0.55)',
        zIndex: 70,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '12vh 16px 16px',
      }}
    >
      <div
        ref={containerRef}
        onClick={e => e.stopPropagation()}
        className="sk-box shadow"
        style={{
          background: 'var(--paper-2)',
          width: 'min(94vw, 560px)',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            borderBottom: '1.4px dashed var(--ink)',
            background: 'var(--paper)',
          }}
        >
          <span style={{ fontSize: 16 }} aria-hidden>🔍</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search problems by title, skill, or tag…"
            aria-label="Search problems"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--ink)',
              padding: '4px 0',
            }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="sk-btn ghost"
            style={{ fontSize: 14, padding: '2px 8px' }}
          >
            ✕
          </button>
        </div>

        <div ref={listRef} className="scroll-y" style={{ flex: 1, padding: 8 }}>
          {results.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 32,
                color: 'var(--ink-faint)',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
              }}
            >
              No problems match "{query}".
            </div>
          ) : (
            results.map((r, idx) => (
              <SearchRow
                key={r.id}
                entry={r}
                focused={idx === focusIdx}
                dataIdx={idx}
                onClick={() => {
                  onClose();
                  navigate(`/problem/${r.id}`);
                }}
                onHover={() => setFocusIdx(idx)}
              />
            ))
          )}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 14,
            padding: '6px 14px',
            borderTop: '1px solid var(--paper-3)',
            background: 'var(--paper)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10,
            color: 'var(--ink-faint)',
          }}
        >
          <span>↑↓ navigate</span>
          <span>enter to open</span>
          <span>esc to close</span>
          <span style={{ flex: 1 }} />
          <span>{results.length} result{results.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
};

interface RowProps {
  entry: CatalogueEntry;
  focused: boolean;
  dataIdx: number;
  onClick: () => void;
  onHover: () => void;
}

const SearchRow: React.FC<RowProps> = ({ entry, focused, dataIdx, onClick, onHover }) => (
  <div
    data-row-idx={dataIdx}
    onClick={onClick}
    onMouseEnter={onHover}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 10px',
      marginBottom: 4,
      borderRadius: 4,
      cursor: 'pointer',
      background: focused ? 'var(--w-cream)' : 'transparent',
      outline: focused ? '2px solid var(--w-coral)' : 'none',
      outlineOffset: -2,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {entry.title}
      </div>
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 10,
          color: 'var(--ink-faint)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {TYPE_LABEL[entry.type] ?? entry.type} · {entry.skillId}
        {entry.tags && entry.tags.length > 0 && ` · ${entry.tags.slice(0, 3).join(', ')}`}
      </div>
    </div>
    <span className={`sk-pill ${DIFF_PILL[entry.difficulty] ?? ''}`} style={{ fontSize: 10 }}>
      {entry.difficulty.toLowerCase()}
    </span>
  </div>
);

export default SearchModal;
